import { useState, useEffect, useCallback } from 'react';
import { useAppSettings } from './useAppSettings';
import useApi from '../api/useApi';
import { SpecificRequirement } from '../types/ProductTypes';

/**
 * Custom hook for fetching all available IBC Specific Requirements
 * These are used for the dropdown when editing product categories
 */
const useIbcSpecificRequirements = () => {
    const { getApiData } = useApi();
    const { settings: appSettings } = useAppSettings();
    const [allIbcRequirements, setAllIbcRequirements] = useState<SpecificRequirement[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchIbcRequirements = useCallback(async () => {
        const apiUrl = appSettings?.api;
        if (!apiUrl) {
            console.warn('[useIbcSpecificRequirements] API URL not configured');
            return;
        }

        try {
            setIsLoading(true);
            setError(null);

            console.log('[useIbcSpecificRequirements] Fetching IBC specific requirements...');

            const response = await getApiData({
                endpoint: `${apiUrl}GetSpecificRequirements`,
                queryParam: '?category=ibc',
            });

            // Map from API response (PascalCase) to camelCase
            const mappedRequirements: SpecificRequirement[] = (response || []).map((req: any) => ({
                id: req.Id ?? req.id,
                paragraphNumber: req.ParagraphNumber ?? req.paragraphNumber,
                paragraphHeading: req.ParagraphHeading ?? req.paragraphHeading,
                text: req.Text ?? req.text,
                lastUpdatedUtc: req.LastUpdatedUtc ?? req.lastUpdatedUtc,
            }));

            console.log(`[useIbcSpecificRequirements] Loaded ${mappedRequirements.length} IBC requirements`);
            setAllIbcRequirements(mappedRequirements);
        } catch (err: any) {
            console.error('[useIbcSpecificRequirements] Error fetching IBC requirements:', err);
            setError(err.message || 'Failed to load IBC requirements');
        } finally {
            setIsLoading(false);
        }
    }, [appSettings?.api, getApiData]);

    useEffect(() => {
        fetchIbcRequirements();
    }, [fetchIbcRequirements]);

    return {
        allIbcRequirements,
        isLoading,
        error,
        refetch: fetchIbcRequirements,
    };
};

export default useIbcSpecificRequirements;
